import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import ErrorBoundary from './ErrorBoundary';
import { industryService, assessmentService } from '../utils/apiService';
import ConversionOptimizedResults from './ConversionOptimizedResults';
import { Skeleton } from './ui/skeleton';
import toast from 'react-hot-toast';
import logger from '../utils/logger';
import { INITIAL_FACILITY_INFO, INITIAL_ENERGY_BASELINE } from '../utils/assessment/defaults';
import { buildAssessmentPayload } from '../utils/assessment/transformers';
import { useAssessmentQuestions } from '../hooks/useAssessment/useAssessmentQuestions';
import { useAssessmentSteps } from '../hooks/useAssessment/useAssessmentSteps';
import { useIndustryData } from '../hooks/useAssessment/useIndustryData';
import { useAssessmentSubmission } from '../hooks/useAssessment/useAssessmentSubmission';
import { useConsultantGuidance } from '../hooks/useAssessment/useConsultantGuidance';
import AssessmentHeader from './Assessment/AssessmentHeader';
import AssessmentStepper from './Assessment/AssessmentStepper';
import AssessmentFooterActions from './Assessment/AssessmentFooterActions';
import AssessmentStep1FacilityInfo from './Assessment/AssessmentStep1FacilityInfo';
import AssessmentStep2IndustrySelection from './Assessment/AssessmentStep2IndustrySelection';
import AssessmentStep2EnergyBaseline from './Assessment/AssessmentStep2EnergyBaseline';
import AssessmentStep3Questions from './Assessment/AssessmentStep3Questions';
import AssessmentStep4Summary from './Assessment/AssessmentStep4Summary';

const Assessment = ({ type }) => {
  const { availableIndustries, state, actions } = useApp();
  const { user, isAuthenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [facilityInfo, setFacilityInfo] = useState(INITIAL_FACILITY_INFO);
  const [subIndustries, setSubIndustries] = useState({});
  const [majorIndustries, setMajorIndustries] = useState([]);
  const [availableSubIndustries, setAvailableSubIndustries] = useState([]);
  const [energyBaseline, setEnergyBaseline] = useState(INITIAL_ENERGY_BASELINE);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const { questions, isLoading: isQuestionsLoading } = useAssessmentQuestions({
    step,
    subIndustryId: facilityInfo.subIndustryId,
    majorIndustry: facilityInfo.majorIndustry,
    region: facilityInfo.region || 'naics',
    setError
  });

  const { goToNextStep, goToPreviousStep } = useAssessmentSteps({
    step,
    setStep,
    facilityInfo,
    energyBaseline,
    questions,
    answers,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    setError
  });
  const {
    subIndustries: subIndustryMap,
    majorIndustries: majorIndustryList,
    availableSubIndustries: availableSubIndustryList
  } = useIndustryData({
    facilityInfo,
    setFacilityInfo
  });

  // Sync local state with industry hook results
  useEffect(() => {
    setSubIndustries(subIndustryMap);
  }, [subIndustryMap]);

  useEffect(() => {
    setMajorIndustries(majorIndustryList);
  }, [majorIndustryList]);

  useEffect(() => {
    setAvailableSubIndustries(availableSubIndustryList);
  }, [availableSubIndustryList]);

  // Auto-fill user information when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      logger.log('Auto-filling user information:', user);
      logger.log('User ID:', user.user_id, user.id, user);
      setFacilityInfo(prev => ({
        ...prev,
        email: user.email || prev.email,
        contactName: user.name || user.full_name || prev.contactName,
        companyName: user.company || user.organization || prev.companyName
      }));
    }
  }, [isAuthenticated, user]);


  const handleFacilityInfoChange = (field, value) => {
    setFacilityInfo(prev => {
      const updated = {
        ...prev,
        [field]: value
      };

      // When major industry is selected, update the region based on the selected industry
      if (field === 'majorIndustry' && value) {
        // Value format: "industryId::region" or "industryName::region"
        const [industryIdentifier, region] = value.includes('::') 
          ? value.split('::') 
          : [value, null];
        
        let selectedIndustry = null;
        let normalizedRegion = 'naics';
        
        if (region) {
          // Region is explicitly in the value
          normalizedRegion = region.toLowerCase();
          // Find industry by ID or name, BUT also match the region to avoid finding wrong industry
          selectedIndustry = majorIndustries.find(ind => {
            if (typeof ind === 'string') return false;
            const id = ind.id || '';
            const name = ind.name || '';
            const indRegion = (ind.region || 'naics').toLowerCase();
            // Match both identifier AND region
            return (id === industryIdentifier || name === industryIdentifier) && indRegion === normalizedRegion;
          });
          
          // If not found with region match, try without region (fallback for edge cases)
          if (!selectedIndustry) {
            selectedIndustry = majorIndustries.find(ind => {
              if (typeof ind === 'string') return false;
              const id = ind.id || '';
              const name = ind.name || '';
              return id === industryIdentifier || name === industryIdentifier;
            });
          }
        } else {
          // Fallback: find by name (legacy support)
          selectedIndustry = majorIndustries.find(ind => {
            const name = typeof ind === 'string' ? ind : ind.name;
            return name === value;
          });
          
          if (selectedIndustry && typeof selectedIndustry !== 'string') {
            normalizedRegion = (selectedIndustry.region || 'naics').toLowerCase();
          }
        }
        
        // Extract industry name from selected industry
        const industryName = selectedIndustry && typeof selectedIndustry !== 'string'
          ? selectedIndustry.name
          : (industryIdentifier.includes('::') ? industryIdentifier.split('::')[0] : industryIdentifier);
        
        updated.region = normalizedRegion;
        updated.majorIndustry = industryName; // Store just the name
        logger.log(`✅ Updated region to ${normalizedRegion} for industry: ${industryName} (ID: ${selectedIndustry?.id || 'N/A'})`);

        // Clear sub-industry when major industry changes
        updated.subIndustry = '';
        updated.subIndustryId = null;
        updated.industry = '';
      }

      return updated;
    });
  };

  const handleInputChange = (field) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const value = e.target.value;
    handleFacilityInfoChange(field, value);
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const {
    requestingConsultant,
    showGuidanceModal,
    userHasChosen,
    handleRequestConsultantGuidance,
    setShowGuidanceModal,
    setUserHasChosen
  } = useConsultantGuidance({
    type,
    navigate
  });

  const { isSubmitting, handleSubmitAssessment, handleSave } = useAssessmentSubmission({
    facilityInfo,
    energyBaseline,
    answers,
    questions,
    user,
    type,
    region: facilityInfo.region || state.region || 'naics',
    navigate,
    setError
  });

  // SKELETON LOADING STATE with dark mode
  if (isQuestionsLoading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
            <Skeleton className="h-8 w-3/4 mb-4 dark:bg-gray-700" />
            <Skeleton className="h-4 w-1/2 mb-8 dark:bg-gray-700" />

            <div className="space-y-6">
              <div>
                <Skeleton className="h-4 w-32 mb-2 dark:bg-gray-700" />
                <Skeleton className="h-10 w-full dark:bg-gray-700" />
              </div>
              <div>
                <Skeleton className="h-4 w-32 mb-2 dark:bg-gray-700" />
                <Skeleton className="h-10 w-full dark:bg-gray-700" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Skeleton className="h-4 w-32 mb-2 dark:bg-gray-700" />
                  <Skeleton className="h-10 w-full dark:bg-gray-700" />
                </div>
                <div>
                  <Skeleton className="h-4 w-32 mb-2 dark:bg-gray-700" />
                  <Skeleton className="h-10 w-full dark:bg-gray-700" />
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-between">
              <Skeleton className="h-10 w-24 dark:bg-gray-700" />
              <Skeleton className="h-10 w-24 dark:bg-gray-700" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalSteps = 5;
  const progress = step === 1 ? 15 : step === 2 ? 30 : step === 2.5 ? 45 : step === 3 ? 45 + (currentQuestionIndex / questions.length) * 35 : step === 4 ? 90 : 100;

  return (
    <ErrorBoundary>
      {/* Toaster removed - using global Toaster from App.js */}
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <AssessmentHeader
            type={type}
            setShowGuidanceModal={setShowGuidanceModal}
            setUserHasChosen={setUserHasChosen}
          />

          <AssessmentStepper
            step={step}
            currentQuestionIndex={currentQuestionIndex}
            questions={questions}
            progress={progress}
          />

          {/* Error Display */}
          {error && (() => {
            // Check if error is a raw dict string and extract message
            const isRawDictString = typeof error === 'string' && (error.includes("'error'") || error.includes('"error"') || error.includes("'message'"));
            let displayError = error;
            
            if (isRawDictString) {
              // Try to extract message from the string
              const messageMatch = error.match(/'message'\s*:\s*"((?:[^"\\]|\\.)*)"/) || 
                                  error.match(/'message'\s*:\s*'((?:[^'\\]|\\.)*)'/) ||
                                  error.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/);
              if (messageMatch && messageMatch[1]) {
                displayError = messageMatch[1];
              } else {
                displayError = 'You have already submitted this assessment. Please view your existing results in the Dashboard.';
              }
            }
            
            return (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                <div className="flex">
                  <svg className="w-5 h-5 text-red-400 dark:text-red-500 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.96-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  {typeof displayError === 'string' ? (
                    <p className="text-red-700 dark:text-red-200">{displayError}</p>
                  ) : (
                    <div className="flex-1 text-red-700 dark:text-red-200">{displayError}</div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Step 1: Facility Information */}
          {step === 1 && (
            <AssessmentStep1FacilityInfo
              facilityInfo={facilityInfo}
              handleInputChange={handleInputChange}
              majorIndustries={majorIndustries}
            />
          )}

          {/* Step 2: Sub-Industry Selection */}
          {step === 2 && (
            <AssessmentStep2IndustrySelection
              facilityInfo={facilityInfo}
              handleInputChange={handleInputChange}
              availableSubIndustries={availableSubIndustries}
            />
          )}

          {/* Step 2.5: Energy Baseline Data */}
          {step === 2.5 && (
            <AssessmentStep2EnergyBaseline
              energyBaseline={energyBaseline}
              setEnergyBaseline={setEnergyBaseline}
            />
          )}

          {/* Step 3: Questions */}
          {step === 3 && questions.length > 0 && (
            <AssessmentStep3Questions
              questions={questions}
              currentQuestionIndex={currentQuestionIndex}
              answers={answers}
              handleAnswerChange={handleAnswerChange}
            />
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <AssessmentStep4Summary
              facilityInfo={facilityInfo}
              energyBaseline={energyBaseline}
              answers={answers}
              questions={questions}
            />
          )}

          {/* Step 5: Conversion-Optimized Success */}
          {step === 5 && (
            <ConversionOptimizedResults
              assessmentData={{
                facility_info: facilityInfo,
                industry: facilityInfo.majorIndustry,
                assessment_type: type
              }}
              energyBaseline={energyBaseline}
              potentialSavings={75000}
              onUpgrade={() => {
                window.location.href = '/upgrade-to-professional';
              }}
            />
          )}

          {/* Navigation */}
          {step < 5 && (
            <AssessmentFooterActions
              step={step}
              goToNextStep={goToNextStep}
              goToPreviousStep={goToPreviousStep}
              handleSubmitAssessment={handleSubmitAssessment}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </div>

      {/* Guidance Choice Modal - Shows at step 1 before form */}
      {showGuidanceModal && !userHasChosen && step === 1 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Welcome to Your {type} Assessment
              </h2>

              {/* Close Button */}
              <button
                onClick={() => {
                  setShowGuidanceModal(false);
                  navigate("/dashboard");
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 
               transition-colors duration-200 p-1 rounded-full focus:outline-none 
               focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                aria-label="Close modal"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>


            <div className="p-6 space-y-4">
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Choose how you'd like to get started with your energy assessment:
              </p>

              {/* Option 1: Self-Guided */}
              <div className="border-2 border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:border-emerald-500 dark:hover:border-emerald-400 transition-all cursor-pointer group"
                onClick={() => {
                  setShowGuidanceModal(false);
                  setUserHasChosen(true);
                }}>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 dark:group-hover:bg-emerald-900/50 transition-colors">
                      <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Self-Guided Assessment
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Fill out the assessment form yourself and complete at your own pace. Perfect if you're familiar with energy management and want to work independently.
                    </p>
                    <div className="mt-3 flex items-center text-emerald-600 dark:text-emerald-400 text-sm font-medium">
                      <span>Continue to form</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Option 2: Request Consultant Guidance */}
              <div className="border-2 border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:border-orange-500 dark:hover:border-orange-400 transition-all cursor-pointer group"
                onClick={handleRequestConsultantGuidance}>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center group-hover:bg-orange-200 dark:group-hover:bg-orange-900/50 transition-colors">
                      <svg className="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                      Get Consultant Guidance
                      <span className="ml-2 px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full">
                        Recommended
                      </span>
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Skip the form and request help from your assigned consultant. They'll schedule a guided session and walk you through the entire assessment with personalized support.
                    </p>
                    {requestingConsultant ? (
                      <div className="mt-3 flex items-center text-orange-600 dark:text-orange-400 text-sm font-medium">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 mr-2"></div>
                        <span>Sending request...</span>
                      </div>
                    ) : (
                      <div className="mt-3 flex items-center text-orange-600 dark:text-orange-400 text-sm font-medium">
                        <span>Request session</span>
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Choosing consultant guidance?</strong> Your assigned consultant will be notified immediately. You'll be taken to your dashboard where you can see the pending request and join once they're ready to start.
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                Please choose one option to proceed with your assessment
              </p>
            </div>
          </div>
        </div>
      )}
    </ErrorBoundary>
  );
};

export default Assessment;
